// src/edu-world/systems/micro-interactions.ts
// Micro-interaction system for small fun discoveries in the world.
// Standalone module — will be wired into the orchestrator later.
// First implementation: pond splash (caring action category).
// Requirements: 23.1, 23.3

import Phaser from 'phaser';

// ─── Types ───

export interface InteractionZone {
  id: string;
  position: { x: number; y: number };
  radius: number;
  category: 'discovery' | 'caring' | 'sensory' | 'foreshadowing' | 'collectible';
  cooldownMs: number;
  onTrigger: (scene: Phaser.Scene, position: { x: number; y: number }) => void;
}

// ─── MicroInteractionManager ───

/**
 * Manages interactive zones in the world that respond to player taps.
 * Each zone has a position, radius, category, cooldown, and trigger callback.
 * The manager handles hit detection, cooldown tracking, and animation triggering.
 */
export class MicroInteractionManager {
  private scene: Phaser.Scene;
  private zones: Map<string, InteractionZone> = new Map();
  private cooldowns: Map<string, number> = new Map(); // zone id → timestamp when cooldown expires
  private inputZone: Phaser.GameObjects.Zone | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Register an interactive zone in the world.
   */
  registerZone(zone: InteractionZone): void {
    this.zones.set(zone.id, zone);
    this.cooldowns.set(zone.id, 0);
  }

  /**
   * Attempt to trigger an interaction at a given world position.
   * Checks if the tap falls within any registered zone and respects cooldowns.
   * Returns true if an interaction was triggered, false otherwise.
   */
  tryInteraction(worldX: number, worldY: number): boolean {
    const now = Date.now();

    for (const [id, zone] of this.zones) {
      const dx = worldX - zone.position.x;
      const dy = worldY - zone.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance <= zone.radius) {
        // Check cooldown
        const cooldownExpiry = this.cooldowns.get(id) ?? 0;
        if (now < cooldownExpiry) {
          // Still in cooldown — ignore tap
          return false;
        }

        // Set cooldown
        this.cooldowns.set(id, now + zone.cooldownMs);

        // Trigger the interaction
        zone.onTrigger(this.scene, zone.position);
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a specific zone is currently in cooldown.
   */
  isOnCooldown(zoneId: string): boolean {
    const expiry = this.cooldowns.get(zoneId) ?? 0;
    return Date.now() < expiry;
  }

  /**
   * Get all registered zones.
   */
  getZones(): InteractionZone[] {
    return Array.from(this.zones.values());
  }

  /**
   * Remove a zone by id.
   */
  removeZone(zoneId: string): void {
    this.zones.delete(zoneId);
    this.cooldowns.delete(zoneId);
  }
}

// ─── Pond Splash Interaction ───

/**
 * Creates a pond splash micro-interaction zone.
 * When triggered:
 * - Blue ripple circles expand outward (splash effect) within 300ms
 * - A small green circle (frog) hops up, pauses, and dips back
 * - Optional splash sound plays if Phaser sound manager is available
 *
 * Category: caring action
 * Cooldown: 10 seconds
 *
 * @param position - The pond's world position (approximately 950, 680)
 * @param radius - Tap detection radius (default 60px)
 */
export function createPondSplashZone(
  position: { x: number; y: number } = { x: 687, y: 210 },
  radius: number = 60
): InteractionZone {
  return {
    id: 'pond-splash',
    position,
    radius,
    category: 'caring',
    cooldownMs: 10000, // 10 seconds
    onTrigger: (scene: Phaser.Scene, pos: { x: number; y: number }) => {
      playPondSplash(scene, pos);
    },
  };
}

/**
 * Plays the pond splash animation sequence:
 * 1. Ripple circles expanding outward (blue, within 300ms start)
 * 2. Small frog appears, hops up, croaks (pauses), dips back down
 * 3. Optional splash sound effect
 */
function playPondSplash(scene: Phaser.Scene, position: { x: number; y: number }): void {
  const { x, y } = position;

  // ── Play splash sound (optional, graceful skip if unavailable) ──
  try {
    if (scene.sound && scene.sound.add) {
      // Only play if the sound key is loaded; skip silently if not
      if (scene.cache?.audio?.exists('splash')) {
        const splash = scene.sound.add('splash', { volume: 0.4 });
        splash.play();
      }
    }
  } catch {
    // Sound not available — continue with visuals only
  }

  // ── Ripple circles (3 expanding rings) ──
  for (let i = 0; i < 3; i++) {
    const ripple = scene.add.circle(x, y, 8, 0x4fc3f7, 0);
    ripple.setStrokeStyle(2, 0x4fc3f7);
    ripple.setDepth(10);
    ripple.setAlpha(0.8);

    scene.tweens.add({
      targets: ripple,
      radius: 30 + i * 18,
      alpha: 0,
      duration: 600 + i * 150,
      delay: i * 100, // staggered start, first ripple within 300ms
      ease: 'Sine.easeOut',
      onUpdate: () => {
        // Phaser circles need manual scale for "radius" expansion
        const scale = (30 + i * 18) / 8;
        ripple.setScale(1 + (scale - 1) * (1 - ripple.alpha / 0.8));
      },
      onComplete: () => {
        ripple.destroy();
      },
    });
  }

  // ── Water droplets (small circles shooting up) ──
  for (let d = 0; d < 4; d++) {
    const droplet = scene.add.circle(
      x + Phaser.Math.Between(-15, 15),
      y,
      3,
      0x81d4fa
    );
    droplet.setDepth(11);
    droplet.setAlpha(0.9);

    scene.tweens.add({
      targets: droplet,
      y: y - Phaser.Math.Between(20, 45),
      x: droplet.x + Phaser.Math.Between(-10, 10),
      alpha: 0,
      duration: 400 + d * 50,
      delay: 50 + d * 40,
      ease: 'Quad.easeOut',
      onComplete: () => {
        droplet.destroy();
      },
    });
  }

  // ── Frog appears briefly ──
  // Use real sapo.png image
  const frog = scene.add.image(x + 25, y + 10, 'sapo');
  frog.setScale(0.035); // ~44px from 1254
  frog.setDepth(12);
  frog.setAlpha(0);

  // Frog hop up sequence
  // Appear after splash starts
  scene.tweens.add({
    targets: frog,
    alpha: 1,
    duration: 200,
    delay: 400,
    ease: 'Sine.easeOut',
  });

  // Hop up
  scene.tweens.add({
    targets: frog,
    y: y - 20,
    duration: 300,
    delay: 500,
    ease: 'Quad.easeOut',
    yoyo: true,
    hold: 600, // pause at top (croak moment)
  });

  // Croak indicator (small text that appears during pause)
  const croak = scene.add.text(x + 40, y - 20, '', {
    fontSize: '16px',
  }).setOrigin(0.5).setDepth(14).setAlpha(0);

  scene.tweens.add({
    targets: croak,
    alpha: 1,
    scale: { from: 0.5, to: 1.1 },
    duration: 200,
    delay: 800,
    ease: 'Back.easeOut',
  });

  // Frog dips back and disappears
  scene.tweens.add({
    targets: frog,
    alpha: 0,
    duration: 300,
    delay: 1500,
    ease: 'Sine.easeIn',
    onComplete: () => {
      frog.destroy();
    },
  });

  // Clean up croak emoji
  scene.tweens.add({
    targets: croak,
    alpha: 0,
    duration: 200,
    delay: 1400,
    ease: 'Sine.easeIn',
    onComplete: () => {
      croak.destroy();
    },
  });

  // ── Play frog croak sound (optional) ──
  scene.time.delayedCall(800, () => {
    try {
      if (scene.sound && scene.sound.add && scene.cache?.audio?.exists('croak')) {
        const croakSound = scene.sound.add('croak', { volume: 0.3 });
        croakSound.play();
      }
    } catch {
      // Sound not available — visuals only
    }
  });
}

// ─── Bench Butterfly Interaction ───

/**
 * Creates a bench butterfly micro-interaction zone.
 * When triggered:
 * - A colorful orange butterfly (circle) flutters in from the side
 * - Lands on the bench, flaps wings gently
 * - Flies away after ~2 seconds
 *
 * Category: discovery
 * Cooldown: 15 seconds
 *
 * Requirements: 23.2, 23.5
 *
 * @param position - The bench's world position (approximately 530, 80)
 * @param radius - Tap detection radius (default 55px)
 */
export function createBenchButterflyZone(
  position: { x: number; y: number } = { x: 473, y: 190 },
  radius: number = 55
): InteractionZone {
  return {
    id: 'bench-butterfly',
    position,
    radius,
    category: 'discovery',
    cooldownMs: 15000, // 15 seconds
    onTrigger: (scene: Phaser.Scene, pos: { x: number; y: number }) => {
      playBenchButterfly(scene, pos);
    },
  };
}

/**
 * Plays the bench butterfly animation sequence:
 * 1. Butterfly (orange circle) flutters in from the left
 * 2. Lands on the bench, wings flap gently (scale pulse)
 * 3. After 2 seconds, flies up and away to the right
 */
function playBenchButterfly(scene: Phaser.Scene, position: { x: number; y: number }): void {
  const { x, y } = position;

  // ── Butterfly (real image) ──
  const butterfly = scene.add.image(x - 60, y - 30, 'borboleta');
  butterfly.setScale(0.03); // ~38px from 1254
  butterfly.setDepth(12);
  butterfly.setAlpha(0);

  // ── Flutter in from the left ──
  scene.tweens.add({
    targets: butterfly,
    alpha: 1,
    duration: 300,
    ease: 'Sine.easeOut',
  });

  // Flutter path to bench (wavy motion)
  scene.tweens.add({
    targets: butterfly,
    x: x,
    y: y - 5,
    duration: 800,
    delay: 100,
    ease: 'Sine.easeInOut',
  });

  // ── Wing flapping while resting (scale pulse) ──
  scene.tweens.add({
    targets: butterfly,
    scaleX: { from: 0.03, to: 0.02 },
    duration: 400,
    delay: 1000,
    ease: 'Sine.easeInOut',
    yoyo: true,
    repeat: 3,
  });

  // ── Small sparkle on landing ──
  const sparkle = scene.add.text(x + 12, y - 15, '✨', {
    fontSize: '12px',
  }).setOrigin(0.5).setDepth(14).setAlpha(0);

  scene.tweens.add({
    targets: sparkle,
    alpha: 1,
    scale: { from: 0.3, to: 1 },
    duration: 300,
    delay: 900,
    ease: 'Back.easeOut',
  });

  scene.tweens.add({
    targets: sparkle,
    alpha: 0,
    duration: 300,
    delay: 1400,
    ease: 'Sine.easeIn',
    onComplete: () => sparkle.destroy(),
  });

  // ── Fly away after 2 seconds ──
  scene.tweens.add({
    targets: butterfly,
    x: x + 80,
    y: y - 60,
    alpha: 0,
    duration: 700,
    delay: 2200,
    ease: 'Quad.easeIn',
    onComplete: () => butterfly.destroy(),
  });

  // ── Optional sound ──
  try {
    if (scene.sound && scene.sound.add && scene.cache?.audio?.exists('flutter')) {
      const flutter = scene.sound.add('flutter', { volume: 0.25 });
      flutter.play();
    }
  } catch {
    // Sound not available — visuals only
  }
}

// ─── Flower Ladybug Interaction ───

/**
 * Creates a flower ladybug micro-interaction zone.
 * When triggered:
 * - A small red ladybug (red circle with dots) crawls in from the side
 * - Wings open briefly (scale up)
 * - Crawls across and disappears
 *
 * Category: sensory
 * Cooldown: 12 seconds
 *
 * Requirements: 23.2, 23.5
 *
 * @param position - The flower area position (approximately 200, 300)
 * @param radius - Tap detection radius (default 50px)
 */
export function createFlowerLadybugZone(
  position: { x: number; y: number } = { x: 610, y: 490 },
  radius: number = 50
): InteractionZone {
  return {
    id: 'flower-ladybug',
    position,
    radius,
    category: 'sensory',
    cooldownMs: 12000, // 12 seconds
    onTrigger: (scene: Phaser.Scene, pos: { x: number; y: number }) => {
      playFlowerLadybug(scene, pos);
    },
  };
}

/**
 * Plays the flower ladybug animation sequence:
 * 1. Ladybug (red circle with black dots) crawls in from the left
 * 2. Stops briefly, wings open (scale up) with tiny sparkle
 * 3. Crawls across to the right and fades out
 */
function playFlowerLadybug(scene: Phaser.Scene, position: { x: number; y: number }): void {
  const { x, y } = position;

  // ── Ladybug (real image) ──
  const ladybug = scene.add.image(x - 40, y + 10, 'joaninha');
  ladybug.setScale(0.025); // ~31px from 1254
  ladybug.setAngle(-90); // Rotate 90° counter-clockwise so it faces right
  ladybug.setDepth(12);
  ladybug.setAlpha(0);

  // ── Crawl in ──
  scene.tweens.add({
    targets: ladybug,
    alpha: 1,
    duration: 300,
    ease: 'Sine.easeOut',
  });

  // Move to center slowly (crawling motion)
  scene.tweens.add({
    targets: ladybug,
    x: x,
    duration: 1000,
    delay: 200,
    ease: 'Sine.easeInOut',
  });

  // ── Wings open briefly (scale up) ──
  scene.tweens.add({
    targets: ladybug,
    scale: 0.035,
    duration: 400,
    delay: 1300,
    ease: 'Back.easeOut',
    yoyo: true,
    hold: 300,
  });

  // ── Tiny sparkle when wings open ──
  const sparkle = scene.add.text(x + 10, y, '✨', {
    fontSize: '10px',
  }).setOrigin(0.5).setDepth(14).setAlpha(0);

  scene.tweens.add({
    targets: sparkle,
    alpha: 1,
    scale: { from: 0.3, to: 1 },
    duration: 250,
    delay: 1400,
    ease: 'Back.easeOut',
  });

  scene.tweens.add({
    targets: sparkle,
    alpha: 0,
    duration: 200,
    delay: 1800,
    ease: 'Sine.easeIn',
    onComplete: () => sparkle.destroy(),
  });

  // ── Crawl away to the right and fade out ──
  scene.tweens.add({
    targets: ladybug,
    x: x + 50,
    alpha: 0,
    duration: 800,
    delay: 2200,
    ease: 'Sine.easeIn',
    onComplete: () => ladybug.destroy(),
  });

  // ── Optional sound ──
  try {
    if (scene.sound && scene.sound.add && scene.cache?.audio?.exists('ladybug-chirp')) {
      const chirp = scene.sound.add('ladybug-chirp', { volume: 0.2 });
      scene.time.delayedCall(1400, () => chirp.play());
    }
  } catch {
    // Sound not available — visuals only
  }
}
